import { ClipboardEvent, FocusEvent, ReactNode, SyntheticEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Autocomplete,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteRenderInputParams,
  Chip,
  FilterOptionsState,
  TextField
} from '@mui/material';
import HelpOutline from '@mui/icons-material/HelpOutlined';
import classNames from 'classnames/bind';
import { FormikProps } from 'formik';
import FormLabel from './FormLabel';
import { AppInputCustomProps, CustomFieldProps } from './types';
import { validateComplexAutoCompleteProps } from './AutocompleteInput';
import { areArraysEqual } from '@/Infrastructure/Utilities/object.type';

import styles from './Form.module.css';

const classNamesCtx = classNames.bind(styles);

const chipAutocompleteSx = {
  '& .MuiOutlinedInput-root': {
    padding: '2px'
  }
} as const;

/** Width reserved for the +N overflow indicator and end adornment icons when deciding how many chips fit the collapsed row */
const OVERFLOW_BADGE_RESERVE_PX = 56;
/** Trailing space that must remain free for all chips to be considered fitting without a +N indicator */
const COLLAPSED_TRAILING_RESERVE_PX = 32;

interface ChipTagProps {
  key: number;
  className: string;
  disabled: boolean;
  'data-item-index': number;
  tabIndex: -1;
  onDelete: (event: unknown) => void;
}

type GetTagProps = (args: { index: number }) => ChipTagProps;

/** Shared hook connecting a chip input to Formik: current value, dirty/error state, and an immediate commit. */
function useChipFieldState<T>(
  form: FormikProps<never>,
  fieldName: string,
  onChangeExtension?: (value?: unknown) => void
): {
  value: T[] | null;
  commit: (next: T[] | null) => void;
  isDirty: boolean;
  error: string | undefined;
  isErrorState: boolean;
} {
  const { value, initialValue, error } = form.getFieldMeta<T[] | null>(fieldName);
  const { dirty, setFieldTouched, setFieldValue } = form;

  const isDirty = useMemo(() => {
    if (dirty) {
      if (value && initialValue) {
        return !areArraysEqual(value, initialValue);
      }
      return value !== initialValue;
    }
    return false;
  }, [value, initialValue, dirty]);

  const isErrorState = isDirty && Boolean(error);

  const commit = useCallback(
    (next: T[] | null) => {
      void setFieldTouched(fieldName, true);
      void setFieldValue(fieldName, next, true);
      if (onChangeExtension) {
        onChangeExtension(next ?? []);
      }
    },
    [setFieldTouched, setFieldValue, fieldName, onChangeExtension]
  );

  return { value, commit, isDirty, error, isErrorState };
}

/** Focus-driven overlay expansion plus width-aware measurement of how many chips physically fit the collapsed row. */
function useChipOverlay(values: readonly unknown[] | null, displayMax?: number) {
  const anchorRef = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [anchorHeight, setAnchorHeight] = useState<number>();
  const [hiddenCount, setHiddenCount] = useState(0);

  useLayoutEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !values?.length) {
      setHiddenCount(0);
      return;
    }

    const measure = () => {
      const chips = Array.from(anchor.querySelectorAll<HTMLElement>(`.${styles.chip}`));
      chips.forEach((chip) => {
        chip.style.display = '';
      });

      if (expanded || chips.length === 0) {
        setHiddenCount(0);
        return;
      }

      const inputRoot = anchor.querySelector<HTMLElement>('.MuiInputBase-root');
      if (!inputRoot) return;

      const rowWidth = inputRoot.clientWidth;
      const chipEnds = chips.map((chip) => chip.offsetLeft + chip.offsetWidth);

      let visible = chips.length;
      if ((chipEnds.at(-1) ?? 0) + COLLAPSED_TRAILING_RESERVE_PX > rowWidth) {
        const budget = rowWidth - OVERFLOW_BADGE_RESERVE_PX;
        visible = chipEnds.filter((end) => end <= budget).length;
      }
      if (displayMax !== undefined && displayMax < visible) {
        visible = displayMax;
      }

      chips.forEach((chip, index) => {
        chip.style.display = index < visible ? '' : 'none';
      });
      setHiddenCount(chips.length - visible);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(anchor);
    return () => {
      observer.disconnect();
    };
  }, [values, expanded, displayMax]);

  const onAnchorFocus = () => {
    if (!expanded) {
      // Freeze the anchor at its collapsed height so expanding as an overlay never shifts surrounding content
      setAnchorHeight(anchorRef.current?.offsetHeight);
      setExpanded(true);
    }
  };

  const onAnchorBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setExpanded(false);
    }
  };

  return { anchorRef, expanded, anchorHeight, hiddenCount, onAnchorFocus, onAnchorBlur };
}

function buildChipTags(options: {
  values: readonly unknown[];
  getTagProps: GetTagProps;
  getLabel: (option: unknown) => string;
  hiddenCount: number;
  deletable: boolean;
}): ReactNode[] {
  const { values, getTagProps, getLabel, hiddenCount, deletable } = options;

  const tags: ReactNode[] = values.map((option, index) => {
    const { key, ...tagProps } = getTagProps({ index });
    return (
      <Chip
        {...tagProps}
        size='small'
        variant='filled'
        label={getLabel(option)}
        key={key}
        className={styles.chip}
        onDelete={deletable ? tagProps.onDelete : undefined}
      />
    );
  });

  if (hiddenCount > 0) {
    tags.push(
      <span key='chip-overflow-indicator' className={styles['chip-overflow']}>
        +{hiddenCount}
      </span>
    );
  }

  return tags;
}

/**
 * Props shared by the application chip inputs that say nothing about where the value is stored. Formik state
 * reaches these components as plain props, the same way TextField and NumberField are arranged, so either can
 * be used outside a Formik form.
 */
interface AppChipSharedProps extends AppInputCustomProps {
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  variant?: 'outlined' | 'standard' | 'filled';
  /** Hard cap on how many chips may be held */
  maxTags?: number;
  /** Optional cap on how many chips display before the (+N) indicator; by default as many as physically fit */
  displayTagsMax?: number;
}

export interface AppMultiSelectChipProps extends AppChipSharedProps {
  /** Controlled chip values. Null rather than empty, so an unset field is distinguishable from a cleared one. */
  value: string[] | null;
  onValueChange: (next: string[] | null) => void;
  autoCompleteItems?: string[];
  helperText?: string;
}

/**
 * Wrapper around MUI Autocomplete presenting free-entry string values as chips, collapsing to a (+N) indicator
 * when they overflow the row and expanding as an overlay while focused.
 */
export function AppMultiSelectChip({ customProps, ...props }: AppMultiSelectChipProps) {
  const { value, onValueChange, maxTags } = props;
  const { anchorRef, expanded, anchorHeight, hiddenCount, onAnchorFocus, onAnchorBlur } = useChipOverlay(value, props.displayTagsMax);

  const handleChange = useCallback(
    (_event: SyntheticEvent, values: string[] | null) => {
      const next = values && values.length > 0 ? values : null;
      if (maxTags && next && next.length > maxTags) return;
      onValueChange(next);
    },
    [onValueChange, maxTags]
  );

  /** Handle copy-paste events with values separated by LF, CR, or CRLF encoded line breaks */
  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      event.preventDefault();
      const pastedValues = event.clipboardData
        .getData('Text')
        .split(/\r?\n|\r/)
        .map((item) => item.trim())
        .filter((pastedValue) => pastedValue.length > 0);
      const deduped = pastedValues.filter((pastedValue) => !value?.includes(pastedValue));
      if (deduped.length === 0) return;
      const merged = [...(value ?? []), ...deduped];
      onValueChange(maxTags ? merged.slice(0, maxTags) : merged);
    },
    [onValueChange, value, maxTags]
  );

  /** Commit any un-entered text as a chip when the input loses focus */
  const handleInputBlur = useCallback(
    (event: FocusEvent<HTMLInputElement>) => {
      const typed = event.target.value;
      if (!typed) return;
      if (maxTags && (value?.length ?? 0) >= maxTags) return;
      if (value?.includes(typed)) return;
      onValueChange([...(value ?? []), typed]);
    },
    [onValueChange, value, maxTags]
  );

  const renderValue = useCallback(
    (tagValue: readonly string[], getTagProps: GetTagProps) =>
      buildChipTags({
        values: tagValue,
        getTagProps,
        getLabel: (option) => option as string,
        hiddenCount,
        deletable: true
      }),
    [hiddenCount]
  );

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      fullWidth
      slotProps={{
        ...params.slotProps,
        input: {
          ...params.slotProps.input,
          className: classNamesCtx({
            input: true,
            standard: props.variant === 'standard'
          }),
          slotProps: {
            root: {
              className: styles.root
            }
          }
        }
      }}
      variant={props.variant}
      label={
        <FormLabel
          text={customProps.labelText}
          error={customProps.isError}
          errorText={customProps.errorText}
          required={customProps.required}
        />
      }
      placeholder={!value ? props.placeholder : undefined}
      helperText={
        props.helperText ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            <HelpOutline sx={{ fontSize: 14 }} />
            {props.helperText}
          </span>
        ) : undefined
      }
      error={customProps.isError}
      disabled={props.disabled}
      onPaste={handlePaste}
      onBlur={handleInputBlur}
    />
  );

  return (
    <div
      ref={anchorRef}
      className={classNamesCtx({
        'multiselect-anchor': true,
        [props.className ?? '']: Boolean(props.className)
      })}
      style={expanded ? { height: anchorHeight } : undefined}
      onFocus={onAnchorFocus}
      onBlur={onAnchorBlur}
    >
      <Autocomplete
        multiple
        freeSolo
        options={props.autoCompleteItems ?? []}
        value={value ?? []}
        onChange={handleChange}
        clearOnBlur
        renderValue={renderValue}
        renderInput={renderInput}
        className={classNamesCtx({
          input: true,
          dirty: customProps.isDirty,
          error: customProps.isError,
          multiselect: true,
          expanded
        })}
        sx={chipAutocompleteSx}
      />
    </div>
  );
}

function asMultiSelectChipInput() {
  return ({ field, form, onChangeExtension, ...props }: CustomFieldProps) => {
    const { value, commit, isDirty, error, isErrorState } = useChipFieldState<string>(form, field.name, onChangeExtension);

    return (
      <AppMultiSelectChip
        value={value}
        onValueChange={commit}
        autoCompleteItems={props.autoCompleteItems}
        maxTags={props.maxTags}
        displayTagsMax={props.displayTagsMax}
        placeholder={props.placeholder}
        helperText={props.helperText}
        variant={props.variant}
        className={props.className}
        disabled={form.isSubmitting || props.disabled}
        customProps={{
          labelText: props.label ?? '',
          isDirty,
          isError: isErrorState,
          errorText: error,
          required: props.required
        }}
      />
    );
  };
}

export const FormikMultiSelectChip = asMultiSelectChipInput();

export interface AppMultiSelectChipComplexProps extends AppChipSharedProps {
  /** Controlled chip values as the option objects themselves. */
  value: unknown[] | null;
  onValueChange: (next: unknown[] | null) => void;
  autoCompleteComplexItems: unknown[];
  getOptionLabel: (option: unknown) => string;
  isOptionEqualToValue?: (option: unknown, value: unknown) => boolean;
  getOptionDisabled?: (option: unknown) => boolean;
  /** Alternate cap on selections, applied when maxTags is not given */
  max?: number;
  renderOption?: (
    props: React.HTMLAttributes<HTMLLIElement> & {
      key: unknown;
    },
    value: unknown
  ) => ReactNode;
  /** Overrides MUI's option filtering, e.g. when the options are already the result of a server-side search */
  filterOptions?: (options: unknown[], state: FilterOptionsState<unknown>) => unknown[];
  /**
   * Called as the input text changes, for options that come from a search rather than a fixed list. The reason
   * distinguishes typing from MUI replaying or clearing the text after a selection.
   */
  onInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
  isLoading?: boolean;
  /** Overrides MUI's empty-list text, e.g. to say that a search has to be typed first */
  noOptionsText?: string;
  popperClass?: string;
  /**
   * Puts a delete affordance on each chip. Needed when the options come from a search rather than a fixed list, since
   * an earlier selection is no longer in the option list to toggle back off.
   */
  deletableChips?: boolean;
}

/**
 * Wrapper around MUI Autocomplete presenting selected complex objects as chips. Selections are removed through the
 * option list rather than the chips, unless deletableChips puts a delete affordance on the chips themselves.
 */
export function AppMultiSelectChipComplex({ customProps, ...props }: AppMultiSelectChipComplexProps) {
  const { value, onValueChange, getOptionLabel, maxTags, max, deletableChips } = props;
  const { anchorRef, expanded, anchorHeight, hiddenCount, onAnchorFocus, onAnchorBlur } = useChipOverlay(value, props.displayTagsMax);

  const handleChange = useCallback(
    (event: SyntheticEvent, values: unknown[] | null, reason: AutocompleteChangeReason) => {
      // Currently we do not want to allow backspace removing options, if we ever want to change this to a prop flag
      if (event.type === 'keydown' && reason === 'removeOption') {
        return;
      }

      const effectiveMax = maxTags ?? max;
      let next = values ?? [];

      // Selecting past the max keeps the newest selection by dropping the oldest
      if (reason === 'selectOption' && effectiveMax && next.length > effectiveMax) {
        next = next.slice(1);
      }

      onValueChange(next.length > 0 ? next : null);
    },
    [onValueChange, maxTags, max]
  );

  const renderValue = useCallback(
    (tagValue: readonly unknown[], getTagProps: GetTagProps) =>
      buildChipTags({
        values: tagValue,
        getTagProps,
        getLabel: getOptionLabel,
        hiddenCount,
        deletable: deletableChips ?? false
      }),
    [getOptionLabel, hiddenCount, deletableChips]
  );

  const renderInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      fullWidth
      slotProps={{
        ...params.slotProps,
        input: {
          ...params.slotProps.input,
          className: styles.input,
          slotProps: {
            root: {
              className: styles.root
            }
          }
        }
      }}
      variant={props.variant}
      label={
        <FormLabel
          text={customProps.labelText}
          error={customProps.isError}
          errorText={customProps.errorText}
          required={customProps.required}
        />
      }
      placeholder={props.placeholder}
      error={customProps.isError}
      disabled={props.disabled}
    />
  );

  return (
    <div
      ref={anchorRef}
      className={classNamesCtx({
        'multiselect-anchor': true,
        [props.className ?? '']: Boolean(props.className)
      })}
      style={expanded ? { height: anchorHeight } : undefined}
      onFocus={onAnchorFocus}
      onBlur={onAnchorBlur}
    >
      <Autocomplete
        multiple
        disableClearable
        options={props.autoCompleteComplexItems}
        getOptionLabel={getOptionLabel}
        isOptionEqualToValue={props.isOptionEqualToValue}
        getOptionDisabled={props.getOptionDisabled}
        filterOptions={props.filterOptions}
        loading={props.isLoading}
        noOptionsText={props.noOptionsText}
        value={value ?? []}
        onChange={handleChange}
        onInputChange={(_event, text, reason) => {
          props.onInputChange?.(text, reason);
        }}
        renderValue={renderValue}
        renderInput={renderInput}
        renderOption={props.renderOption}
        className={classNamesCtx({
          input: true,
          dirty: customProps.isDirty,
          error: customProps.isError,
          multiselect: true,
          expanded
        })}
        slotProps={{ popper: { className: props.popperClass } }}
        sx={chipAutocompleteSx}
      />
    </div>
  );
}

function asComplexMultiSelectChipInput() {
  return (fieldProps: CustomFieldProps) => {
    if (!validateComplexAutoCompleteProps(fieldProps)) throw new Error('Invalid ComplexAutoComplete Props');

    const { field, form, onChangeExtension, ...props } = fieldProps;

    const { value, commit, isDirty, error, isErrorState } = useChipFieldState<unknown>(form, field.name, onChangeExtension);

    return (
      <AppMultiSelectChipComplex
        value={value}
        onValueChange={commit}
        autoCompleteComplexItems={props.autoCompleteComplexItems}
        getOptionLabel={props.getOptionLabel}
        isOptionEqualToValue={props.isOptionEqualToValue}
        getOptionDisabled={props.getOptionDisabled}
        maxTags={props.maxTags}
        max={props.max}
        displayTagsMax={props.displayTagsMax}
        placeholder={props.placeholder}
        variant={props.variant}
        className={props.className}
        disabled={form.isSubmitting || props.disabled}
        customProps={{
          labelText: props.label ?? '',
          isDirty,
          isError: isErrorState,
          errorText: error,
          required: props.required
        }}
      />
    );
  };
}

export const FormikMultiSelectChipComplex = asComplexMultiSelectChipInput();
